import { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth } from '../auth/AuthContext';
import {
  formatHttpErrorDetails,
  formatThrownErrorDetails,
  friendlyNetworkErrorMessage,
} from '../api/httpErrorFormat';
import { persistStoredRefreshToken } from '../auth/sessionToken';
import { clearRefreshUnavailable404 } from '../api/authRefresh';
import { useI18n } from '../i18n';
import { localizeServerMessage } from '../i18n/localizeServerMessage';
import { useAppNavigation } from '../navigation/AppNavigationContext';
import { persistIdentifierForLoginOption } from './lastLoginStorage';
import { persistLastSignInMethod } from './lastSignInMethod';
import { syncProfileAvatarAfterGoogleLogin, seedProfileFromGoogleLogin } from './profileGoogleAvatar';
import { serverRoleMatchesLoginType } from './authEntryPolicy';
import type { LoginOption } from './loginConstants';
import {
  emailFromGoogleCredential,
  enrichAuthBodyWithGoogleProfile,
  extractRefreshTokenFromGoogleLogin,
  ensureGoogleIdentityInitialized,
  friendlyGoogleLoginFailureSummary,
  googleLoginDebugRequestPayload,
  googleProfileFromCredential,
  isGoogleUserMissingError,
  isSuccessfulGoogleLoginResult,
  loadGoogleIdentityScript,
  postGoogleLoginCredential,
  postGoogleLoginCredentialOnly,
  resolveGoogleOAuthClientId,
  setGoogleSignInCredentialHandler,
  type GoogleIdTokenProfile,
} from './googleSignIn';
import { roleIdFromJwt, profileRoleIdFromData } from '../auth/sessionBookingRole';
import { translateLoginOption } from '../i18n';
import { useLastAppError } from '../errors/LastAppErrorContext';
import {
  classifyNativeGoogleSignInError,
  isNativeGoogleSignInAvailable,
  nativeGoogleSignInErrorBlob,
  requestNativeGoogleIdToken,
} from './nativeGoogleSignIn';

function mountGoogleSignInButton(
  mount: HTMLElement,
  clientId: string,
  language: string,
): void {
  const google = window.google?.accounts?.id;
  if (!google) {
    return;
  }
  mount.replaceChildren();
  ensureGoogleIdentityInitialized(clientId);
  const containerWidth = mount.parentElement?.offsetWidth || mount.offsetWidth || 360;
  google.renderButton(mount, {
    type: 'standard',
    theme: 'outline',
    size: 'large',
    text: 'signin_with',
    shape: 'rectangular',
    logo_alignment: 'left',
    width: Math.max(280, Math.min(420, containerWidth)),
    locale: language === 'he' ? 'he' : 'en',
  });
}

function clickGoogleRenderedButton(mount: HTMLElement | null): boolean {
  if (!mount) {
    return false;
  }
  const inner = mount.querySelector('[role="button"]') as HTMLElement | null;
  if (inner) {
    inner.click();
    return true;
  }
  return false;
}

export type UseGoogleSignInOptions = {
  /** On register pages: sign in existing users; prefill the form when the account is new. */
  registerPrefillOnMiss?: boolean;
  onRegisterPrefill?: (profile: GoogleIdTokenProfile, credential: string) => void;
};

export function useGoogleSignIn(loginType: LoginOption, options: UseGoogleSignInOptions = {}) {
  const { t, language } = useI18n();
  const { applyLoginSuccess, isLoggedIn } = useAuth();
  const { reportError } = useLastAppError();
  const { setView } = useAppNavigation();
  const clientId = resolveGoogleOAuthClientId();
  const isConfigured = Boolean(clientId);
  const buttonMountRef = useRef<HTMLDivElement | null>(null);
  const oneTapAttemptedRef = useRef(false);
  const [submitting, setSubmitting] = useState(false);
  const [errorFriendly, setErrorFriendly] = useState<string | null>(null);
  const [errorDetails, setErrorDetails] = useState<string | null>(null);

  const { registerPrefillOnMiss = false, onRegisterPrefill } = options;
  const onRegisterPrefillRef = useRef(onRegisterPrefill);
  onRegisterPrefillRef.current = onRegisterPrefill;

  const completeGoogleCredential = useCallback(
    async (credential: string) => {
      if (isLoggedIn) {
        return;
      }
      setSubmitting(true);
      setErrorFriendly(null);
      setErrorDetails(null);
      try {
        const result = registerPrefillOnMiss
          ? await postGoogleLoginCredentialOnly(credential, loginType)
          : await postGoogleLoginCredential(credential, loginType);
        if (isSuccessfulGoogleLoginResult(result)) {
          const tokenRoleId =
            roleIdFromJwt(result.accessToken) ?? profileRoleIdFromData(result.parsed);
          if (!serverRoleMatchesLoginType(loginType, tokenRoleId)) {
            setErrorFriendly(
              t('auth.portal.googleWrongServerRole', {
                roleId: String(tokenRoleId ?? '?'),
                type: translateLoginOption(loginType, t),
              }),
            );
            setErrorDetails(
              formatHttpErrorDetails({
                requestUrl: result.urlUsed,
                requestPayload: googleLoginDebugRequestPayload(credential, loginType, result),
                response: result.response,
                responseText: result.responseText,
              }),
            );
            return;
          }
          const googleProfile = googleProfileFromCredential(credential);
          const email = googleProfile.email || emailFromGoogleCredential(credential);
          const loginIdentifier = email || 'google-user';
          const enrichedBody = enrichAuthBodyWithGoogleProfile(result.parsed, googleProfile);
          applyLoginSuccess(enrichedBody, result.accessToken!, loginIdentifier, loginType);
          persistIdentifierForLoginOption(loginType, loginIdentifier);
          persistLastSignInMethod('google');
          syncProfileAvatarAfterGoogleLogin(credential, enrichedBody);
          void seedProfileFromGoogleLogin(credential, enrichedBody);
          const refreshToken = extractRefreshTokenFromGoogleLogin(result.parsed);
          if (refreshToken) {
            persistStoredRefreshToken(refreshToken);
          }
          // Cookie-only Google sessions still need refresh retries; a prior 404 memo would skip them.
          clearRefreshUnavailable404();
          setView('home', { replace: true });
          return;
        }
        if (registerPrefillOnMiss && isGoogleUserMissingError(result)) {
          const googleProfile = googleProfileFromCredential(credential);
          onRegisterPrefillRef.current?.(googleProfile, credential);
          return;
        }
        setErrorFriendly(
          localizeServerMessage(
            friendlyGoogleLoginFailureSummary(result, {
              notEnabled: t('auth.portal.googleNotEnabled'),
              unavailable: t('auth.portal.googleUnavailable'),
              loginTypeMismatch: t('auth.portal.googleLoginTypeMismatch'),
              userNotRegistered: t('auth.portal.googleUserNotRegistered'),
            }),
            t,
          ),
        );
        setErrorDetails(
          formatHttpErrorDetails({
            requestUrl: result.urlUsed,
            requestPayload: googleLoginDebugRequestPayload(credential, loginType, result),
            response: result.response,
            responseText: result.responseText,
          }),
        );
      } catch (err) {
        setErrorFriendly(friendlyNetworkErrorMessage(err));
        setErrorDetails(formatThrownErrorDetails(err));
      } finally {
        setSubmitting(false);
      }
    },
    [applyLoginSuccess, isLoggedIn, loginType, registerPrefillOnMiss, setView, t],
  );

  const completeGoogleCredentialRef = useRef(completeGoogleCredential);
  completeGoogleCredentialRef.current = completeGoogleCredential;

  useEffect(() => {
    setGoogleSignInCredentialHandler((credential) => {
      void completeGoogleCredentialRef.current(credential);
    });
    return () => {
      setGoogleSignInCredentialHandler(null);
    };
  }, []);

  const ensureGoogleButtonMounted = useCallback(async (): Promise<HTMLElement | null> => {
    if (!clientId) {
      return null;
    }
    await loadGoogleIdentityScript();
    const mount = buttonMountRef.current;
    if (!mount || !window.google?.accounts?.id) {
      return null;
    }
    mountGoogleSignInButton(mount, clientId, language);
    return mount;
  }, [clientId, language]);

  const requestGoogleSignIn = useCallback(async () => {
    if (!clientId || isLoggedIn || submitting) {
      return;
    }
    setErrorFriendly(null);
    setErrorDetails(null);
    if (isNativeGoogleSignInAvailable()) {
      setSubmitting(true);
      try {
        const credential = await requestNativeGoogleIdToken(clientId);
        await completeGoogleCredential(credential);
      } catch (err) {
        const kind = classifyNativeGoogleSignInError(err);
        if (kind === 'cancelled') {
          return;
        }
        const friendlyKey =
          kind === 'developerError'
            ? 'auth.portal.googlePlaySigning'
            : kind === 'notReady'
              ? 'auth.portal.googleNativeNotReady'
              : kind === 'noIdToken'
                ? 'auth.portal.googleMissingIdToken'
                : 'auth.portal.googleLoadFailed';
        const details = formatThrownErrorDetails(err);
        setErrorFriendly(t(friendlyKey));
        setErrorDetails(details);
        reportError({
          source: 'google-sign-in',
          message: t(friendlyKey),
          context: { kind, plugin: nativeGoogleSignInErrorBlob(err) },
        });
      } finally {
        setSubmitting(false);
      }
      return;
    }
    try {
      const mount = await ensureGoogleButtonMounted();
      if (!mount) {
        setErrorFriendly(t('auth.portal.googleLoadFailed'));
        return;
      }
      if (!clickGoogleRenderedButton(mount)) {
        window.google?.accounts?.id?.prompt();
      }
    } catch {
      setErrorFriendly(t('auth.portal.googleLoadFailed'));
    }
  }, [clientId, completeGoogleCredential, ensureGoogleButtonMounted, isLoggedIn, reportError, submitting, t]);

  useEffect(() => {
    if (!clientId || isLoggedIn || isNativeGoogleSignInAvailable()) {
      return;
    }
    oneTapAttemptedRef.current = false;
    let cancelled = false;
    void ensureGoogleButtonMounted()
      .then((mount) => {
        if (cancelled || !mount) {
          return;
        }
        if (!oneTapAttemptedRef.current) {
          oneTapAttemptedRef.current = true;
          window.setTimeout(() => {
            if (!cancelled && !isLoggedIn) {
              window.google?.accounts?.id?.prompt();
            }
          }, 400);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setErrorFriendly(t('auth.portal.googleLoadFailed'));
        }
      });
    return () => {
      cancelled = true;
      window.google?.accounts?.id?.cancel?.();
    };
  }, [clientId, ensureGoogleButtonMounted, isLoggedIn, t]);

  return {
    clientId,
    isConfigured,
    buttonMountRef,
    submitting,
    errorFriendly,
    errorDetails,
    requestGoogleSignIn,
    clearErrors: useCallback(() => {
      setErrorFriendly(null);
      setErrorDetails(null);
    }, []),
  };
}
